import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PlaidLink, PlaidLinkOnSuccessMetadata } from "react-plaid-link";
import { getPaymentLinkToken, exchangePublicToken } from "../../api/services";

interface AddBankModalProps {
  open: boolean;
  onClose: () => void;
  onConnected: () => void;
}

/**
 * Modal that runs the Plaid → Stripe bank-link flow:
 *   1. Fetch a Plaid link token.
 *   2. Open Plaid Link.
 *   3. Exchange the public token; backend attaches to Stripe and stores in
 *      `paymentMethod` (plus legacy `paymentAccount`).
 */
export const AddBankModal: React.FC<AddBankModalProps> = ({
  open,
  onClose,
  onConnected,
}) => {
  const { t } = useTranslation();
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [exchanging, setExchanging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // True after an exchange failed. The held Plaid token is spent, so we
  // show a "Try again" prompt that re-links from scratch instead of
  // letting the user re-Authorize a dead token.
  const [failed, setFailed] = useState(false);
  // After Plaid returns its token we hold it locally and show the ACH
  // mandate disclosure. Only after the user clicks Authorize do we send
  // the exchange request — that's what gives Stripe a stored mandate so
  // future off-session purchases AND renewals can charge this bank.
  const [pendingPlaid, setPendingPlaid] = useState<{
    publicToken: string;
    metadata: PlaidLinkOnSuccessMetadata;
  } | null>(null);

  // Reset transient flow state whenever the modal closes, so reopening it
  // always starts clean (no stale error/failed/pending state from a prior
  // attempt).
  useEffect(() => {
    if (open) return;
    setError(null);
    setFailed(false);
    setPendingPlaid(null);
    setExchanging(false);
  }, [open]);

  useEffect(() => {
    if (!open || linkToken) return;
    let cancelled = false;
    setError(null);
    getPaymentLinkToken()
      .then((res) => {
        if (!cancelled) setLinkToken(res.data.link_token);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setError(t("addBankModal.errorInit"));
      });
    return () => {
      cancelled = true;
    };
  }, [open, linkToken, t]);

  if (!open) return null;

  const handleSuccess = (
    publicToken: string,
    metadata: PlaidLinkOnSuccessMetadata,
  ) => {
    // Hold the Plaid result and switch to the disclosure step. The actual
    // exchange (and Stripe mandate) only runs when the user clicks Authorize.
    setPendingPlaid({ publicToken, metadata });
  };

  const handleAuthorize = async () => {
    if (!pendingPlaid) return;
    setExchanging(true);
    setError(null);
    try {
      await exchangePublicToken(
        pendingPlaid.publicToken,
        pendingPlaid.metadata,
        true,
      );
      onConnected();
    } catch (err: any) {
      console.error(err);
      // The Plaid public token is single-use and was just consumed by the
      // failed exchange, so re-clicking Authorize can never succeed. Mark
      // the attempt as failed and drop the spent token — the user must
      // re-link through Plaid, which the "Try again" button below does.
      //
      // Prefer the API's message: the exchange fails for specific reasons
      // (no ACH details on the account, Stripe rejected the token) and the
      // generic copy sends the tenant into a retry loop that fails the same
      // way. Falls back to the generic string when there's no message.
      const apiMessage = err?.response?.data?.message;
      setError(
        typeof apiMessage === "string" && apiMessage.trim()
          ? apiMessage
          : t("addBankModal.errorExchange"),
      );
      setFailed(true);
      setPendingPlaid(null);
    } finally {
      setExchanging(false);
    }
  };

  // Reset to the Plaid link step. We also drop the link token so a fresh
  // one is fetched — the previous token's flow is spent.
  const handleRetry = () => {
    setFailed(false);
    setError(null);
    setLinkToken(null);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div className="w-full max-w-md rounded-xl bg-white shadow-lg">
        <div className="border-b border-gray-100 px-6 py-4">
          <h4 className="font-semibold text-primary">
            {pendingPlaid
              ? t("addBankModal.mandate.title")
              : t("addBankModal.title")}
          </h4>
        </div>

        {failed ? (
          // Exchange failed and the Plaid token is spent. Offer a clean
          // re-link rather than a dead re-Authorize.
          <div className="p-6">
            <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
              {error || t("addBankModal.errorExchange")}
            </div>
            <div className="flex justify-end gap-2">
              <button
                className="cursor-pointer rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                onClick={onClose}
              >
                {t("addBankModal.cancel")}
              </button>
              <button
                type="button"
                className="cursor-pointer rounded-lg bg-primary px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-light"
                onClick={handleRetry}
              >
                {t("addBankModal.tryAgain")}
              </button>
            </div>
          </div>
        ) : pendingPlaid ? (
          // Step 2: ACH mandate disclosure. Tenant must explicitly authorize
          // before we send anything to Stripe.
          <div className="p-6">
            <p className="mb-4 text-sm text-gray-700">
              {t("addBankModal.mandate.intro")}
            </p>
            <div className="mb-4 rounded-lg bg-gray-50 p-4 text-xs leading-relaxed text-muted">
              {t("addBankModal.mandate.disclosure")}
            </div>
            {error && (
              <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
                {error}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                className="cursor-pointer rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => {
                  setPendingPlaid(null);
                  setError(null);
                }}
                disabled={exchanging}
              >
                {t("addBankModal.mandate.cancel")}
              </button>
              <button
                type="button"
                className="cursor-pointer rounded-lg bg-primary px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleAuthorize}
                disabled={exchanging}
              >
                {exchanging
                  ? t("addBankModal.connecting")
                  : t("addBankModal.mandate.authorize")}
              </button>
            </div>
          </div>
        ) : (
          // Step 1: Plaid link.
          <div className="p-6 text-center">
            <p className="mb-2">
              <img alt="Plaid" src="/assets/plaid.svg" className="mx-auto" />
            </p>
            <p className="text-sm text-muted mb-4">{t("addBankModal.intro")}</p>
            {error && (
              <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
                {error}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer"
                onClick={onClose}
                disabled={exchanging}
              >
                {t("addBankModal.cancel")}
              </button>
              {linkToken ? (
                <PlaidLink
                  token={linkToken}
                  onSuccess={handleSuccess}
                  onExit={(err, metadata) =>
                    console.error("Plaid exit:", err, metadata)
                  }
                  style={{
                    backgroundColor: "#ffc107",
                    color: "#000",
                    padding: "0.625rem 1.5rem",
                    borderRadius: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    border: 0,
                    cursor: "pointer",
                  }}
                  className="transition-colors hover:!bg-accent-hover"
                >
                  {t("addBankModal.connect")}
                </PlaidLink>
              ) : (
                <button
                  type="button"
                  style={{
                    backgroundColor: "#ffc107",
                    color: "#000",
                    padding: "0.625rem 1.5rem",
                    borderRadius: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    border: 0,
                    cursor: "not-allowed",
                    opacity: 0.5,
                  }}
                  disabled
                >
                  {t("addBankModal.loading")}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddBankModal;
